import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { CheckCircle2, FileText, Stethoscope, ShieldCheck, MessageCircle, Home, ArrowRight, Clock, Sparkles, Phone } from "lucide-react";
import Navbar from "../components/Navbar";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ga } from "../lib/analytics";
import ReferralCard from "../components/ReferralCard";

const STAGES = [
  { key: "submitted", label: "Submitted",       icon: CheckCircle2,
    blurb: "Payment received & case queued." },
  { key: "review",    label: "Doctor Review",    icon: Stethoscope,
    blurb: "Specialist is reviewing your scans + symptoms." },
  { key: "report",    label: "Report Ready",     icon: FileText,
    blurb: "Detailed second opinion delivered to your inbox." },
];

function stageIndexFor(c) {
  if (!c) return 0;
  if (c.status === "completed") return 2;
  if (c.status === "under_review") return 1;
  return 0; // awaiting_review / paid
}

function fireConfettiBurst() {
  // Two staggered cannons for a fuller effect.
  const colors = ["#0ea5e9", "#14b8a6", "#22c55e", "#facc15", "#a855f7"];
  confetti({
    particleCount: 90,
    spread: 75,
    origin: { y: 0.55, x: 0.25 },
    colors,
    scalar: 0.9,
  });
  setTimeout(() => {
    confetti({
      particleCount: 90,
      spread: 75,
      origin: { y: 0.55, x: 0.75 },
      colors,
      scalar: 0.9,
    });
  }, 220);
  // Slow trickle for 1.2 s
  let count = 0;
  const trickle = setInterval(() => {
    if (count++ > 6) { clearInterval(trickle); return; }
    confetti({
      particleCount: 22,
      spread: 60,
      angle: 60 + Math.random() * 60,
      origin: { y: 0.4 + Math.random() * 0.2, x: Math.random() },
      colors,
      scalar: 0.7,
    });
  }, 180);
}

export default function ThankYou() {
  const { cid } = useParams();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    fireConfettiBurst();
  }, []);

  // GA4: fire payment_success exactly once per mount — this is the money metric.
  const reported = useRef(false);
  useEffect(() => {
    if (reported.current || !cid) return;
    reported.current = true;
    ga.paymentSuccess({ consultation_id: cid });
  }, [cid]);

  // GA4 enhanced-ecommerce `purchase` — fires once we know the actual paid
  // amount (after coupon). Dedup is handled inside ga.purchase() so Strict-Mode
  // double-mounts and page reloads do not double-count revenue.
  useEffect(() => {
    if (!c || !cid) return;
    const value = c.fee_after_discount ?? c.fee ?? 0;
    if (!value) return;
    ga.purchase({
      transactionId: cid,
      valueRupees: value,
      priority: c.priority,
      conditionSlug: (c.symptom_tags && c.symptom_tags[0]) || null,
    });
  }, [c, cid]);

  useEffect(() => {
    if (!cid) return;
    let stop = false;
    const load = async () => {
      try {
        const r = await api.get(`/consultations/${cid}`);
        if (!stop) setC(r.data);
      } catch (e) {
        if (!stop) setErr(e.response?.data?.detail || "Could not load case");
      }
    };
    load();
    // Poll every 8 s for status flips while user is on this page.
    const t = setInterval(load, 8000);
    return () => { stop = true; clearInterval(t); };
  }, [cid]);

  if (!user) return <Navigate to="/login" replace />;

  const idx = stageIndexFor(c);
  const slaHours = c?.sla_hours || (c?.priority === "urgent" ? 12 : c?.priority === "video" ? 24 : 48);
  const fee = c?.fee_after_discount ?? c?.fee ?? 0;
  const eta = c?.created_at
    ? new Date(new Date(c.created_at).getTime() + slaHours * 3600 * 1000)
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-slate-50 to-white">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16" data-testid="thankyou-root">
        {/* Sparkle hero */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 shadow-xl shadow-emerald-500/30 ring-4 ring-emerald-100 mb-5 animate-[bounce_1s_ease-in-out_1]"
               data-testid="thankyou-checkmark">
            <CheckCircle2 className="w-10 h-10 text-white" strokeWidth={2.5}/>
          </div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-semibold uppercase tracking-wider">
            <Sparkles className="w-3 h-3"/> Payment confirmed
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mt-4 tracking-tight">
            Thank you{user?.name ? `, ${user.name.split(" ")[0]}` : ""}!
          </h1>
          <p className="text-base text-slate-600 mt-2 max-w-xl mx-auto leading-relaxed">
            Your second opinion is now in safe hands.{" "}
            {c?.doctor_name && <><b className="text-slate-900">{c.doctor_name}</b> will review your case shortly.</>}
          </p>
          {fee > 0 && (
            <div className="mt-3 text-sm text-slate-500">
              Payment of <span className="font-semibold text-slate-900">₹{Number(fee).toLocaleString()}</span> received successfully.
            </div>
          )}
        </div>

        {/* Status tracker */}
        <Card className="mt-8 p-5 sm:p-7 border-slate-200 shadow-sm" data-testid="thankyou-tracker">
          <div className="flex items-center justify-between mb-1">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700">Case progress</div>
              <div className="font-heading text-lg font-semibold text-slate-900 mt-1">
                {STAGES[idx].label}
              </div>
              <p className="text-xs text-slate-500 mt-0.5">{STAGES[idx].blurb}</p>
            </div>
            {eta && idx < 2 && (
              <div className="text-right hidden sm:block">
                <div className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  <Clock className="w-3 h-3"/> ETA
                </div>
                <div className="font-heading text-base font-semibold text-slate-900 mt-0.5">
                  {eta.toLocaleString(undefined, { weekday: "short", hour: "2-digit", minute: "2-digit" })}
                </div>
                <div className="text-[10px] text-slate-500">within {slaHours} h</div>
              </div>
            )}
          </div>

          {/* Stages */}
          <div className="mt-5">
            <div className="grid grid-cols-3 gap-1 sm:gap-3">
              {STAGES.map((s, i) => {
                const Done = i < idx;
                const Active = i === idx;
                const Icon = s.icon;
                return (
                  <div key={s.key} className="flex flex-col items-center text-center" data-testid={`stage-${s.key}`}>
                    <div className={`relative w-11 h-11 sm:w-12 sm:h-12 rounded-full grid place-items-center transition-all ${
                      Done ? "bg-emerald-600 text-white shadow-md shadow-emerald-500/30"
                           : Active ? "bg-gradient-to-br from-blue-600 to-teal-600 text-white shadow-lg shadow-blue-500/30 ring-4 ring-blue-100 animate-pulse"
                                    : "bg-slate-100 text-slate-400"
                    }`}>
                      <Icon className="w-5 h-5"/>
                    </div>
                    <div className={`text-[11px] sm:text-xs font-semibold mt-2 ${Done || Active ? "text-slate-900" : "text-slate-400"}`}>
                      {s.label}
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Connector bars (positioned below to keep mobile tidy) */}
            <div className="relative mt-3 h-1 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full transition-all duration-700"
                style={{ width: `${((idx) / (STAGES.length - 1)) * 100}%` }}
              />
            </div>
          </div>
        </Card>

        {/* Case summary card */}
        <Card className="mt-5 p-5 border-slate-200" data-testid="thankyou-summary">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-700">Case ID</div>
              <div className="font-mono text-sm font-semibold text-slate-900 mt-0.5 truncate" data-testid="thankyou-case-id">
                {cid?.slice(0, 8).toUpperCase()}
              </div>
            </div>
            {c?.priority && (
              <div className="text-right">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Priority</div>
                <div className="font-heading text-sm font-semibold text-slate-900 mt-0.5 capitalize">{c.priority}</div>
              </div>
            )}
            <div className="text-right">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">SLA</div>
              <div className="font-heading text-sm font-semibold text-slate-900 mt-0.5">{slaHours} hours</div>
            </div>
          </div>
          {err && <div className="text-xs text-rose-600 mt-3">{err}</div>}
        </Card>

        {/* Trust + WhatsApp footer */}
        <div className="mt-7 flex flex-col sm:flex-row items-stretch gap-3">
          <Link to="/patient" className="flex-1">
            <Button variant="outline" className="w-full h-11 border-slate-300" data-testid="thankyou-dashboard-btn">
              <Home className="w-4 h-4 mr-2"/> My Dashboard
            </Button>
          </Link>
          {cid && (
            <Link to={`/case/${cid}`} className="flex-1">
              <Button className="w-full h-11 bg-blue-700 hover:bg-blue-800" data-testid="thankyou-case-btn">
                <FileText className="w-4 h-4 mr-2"/> View case details <ArrowRight className="w-4 h-4 ml-1"/>
              </Button>
            </Link>
          )}
        </div>

        {/* Refer & earn */}
        <ReferralCard variant="thankyou" />

        <Card className="mt-5 p-4 sm:p-5 border-emerald-200 bg-emerald-50/60" data-testid="thankyou-help">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 grid place-items-center">
              <ShieldCheck className="w-5 h-5"/>
            </div>
            <div className="text-sm text-emerald-900/90 leading-relaxed">
              <b>Need to add more info?</b> WhatsApp our care team on{" "}
              <a href="https://wa.me/918317674160" className="font-semibold underline decoration-emerald-400 underline-offset-2" data-testid="thankyou-whatsapp-link">
                +91 83176 74160
              </a>
              {" "}or call us anytime. We'll keep you posted on every status change.
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <a href="https://wa.me/918317674160" className="inline-flex items-center gap-2 px-3 h-9 rounded-lg bg-white border border-emerald-300 text-emerald-800 text-xs font-semibold hover:bg-emerald-50">
              <MessageCircle className="w-4 h-4"/> WhatsApp us
            </a>
            <a href="tel:+918317674160" className="inline-flex items-center gap-2 px-3 h-9 rounded-lg bg-white border border-slate-300 text-slate-800 text-xs font-semibold hover:bg-slate-50">
              <Phone className="w-4 h-4"/> Call
            </a>
          </div>
        </Card>
      </div>
    </div>
  );
}
